/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

// tslint:disable:variable-name

import {
  ComponentItemFinderListItem,
  CostCenterFinderListItem,
  formulaValidator,
  getUomQuantityType,
  SalesTaxRateListItem,
} from '@gh/builder-shared';
import {
  CostCenterSupplierFinderListItemDto,
  createUomQuantity,
  Formula,
  GtoUom,
  isFormulaEmpty,
  JeAuthorizeResponseDto,
  JeCcCompleteDto,
  JeCcOrderDetailsDto,
  JeCcOrderDetailsListItemDto,
  JeCcRefDto,
  JeCcSaveDto,
  JeColorSelectionDto,
  JeComponentDto,
  JeComponentItemDto,
  JeComponentSaveDto,
  JeComponentWithItemsDto,
  JeCuttingListItemDto,
  JeDetailsBase,
  JeDetailsDto,
  JeDirtyDataDto,
  JeGetComponentsResponseDto,
  JeItemDto,
  JeItemSaveDto,
  JeOrderGeneratedDto,
  JeStrDto,
  JeTopDto,
  JeTopRefItemDto,
  JeTopSaveDto,
  JeUomRefItemDto,
  JeVariationStatus,
  JeVariationType,
  JobOrderDelivery,
  JobOrderDetailsDto,
  normalizeTakeOffParams,
  TakeOffParamDto,
  TOP_CODE_CONTRACT_PRICE,
  UomDetailsDto,
} from '@gh/builder-shared-data';
import {
  bindUniqueId,
  DateTimeTypes,
  EntityConfig,
  entityStateMerger,
  entityValidatorByEntityConfig,
  errorsEntityState,
  fieldStateByEntityConfig,
  fieldValidatorByEntityConfig,
  fieldValidatorFrom,
  nestedArrayValidator,
  NumberTypes,
  setCreatedDate,
  setEntityField,
  TextTypes,
} from '@gh/common-shared';
import { ID, WithCreatedDate, WithDirtyType, WithEntityState, WithUniqueId, WithVersion } from '@gh/common-shared-data';
import { createEnum, EnumSet, Quantity, WithOptimisticLock } from '@gh/core-data';
import { cast, composeComparators, currentDate, isNotNil, valueComparator } from '@gh/core-util';
import { compact, flatMap, isEmpty, isNil, keyBy, map, omit, pick, uniqueId } from 'lodash';
import { jobOrderDetailsFromDto, jobOrderDetailsToDto } from './job-order-details.model';

export const JE_CCC_ID = 'ccc';

// tslint:disable-next-line:variable-name
export const JeItemType = createEnum('JE_ITEM_TYPE', {
  Item: 'ITEM',
  Component: 'Component',
  SalesTax: 'SALES_TAX',
  Divider: 'DIVIDER',
});
export type JeItemType = EnumSet<typeof JeItemType>;

export const JeCcFilter = createEnum('SHOW_CC', {
  ShowAll: 'SHOW_ALL',
  ShowNotZero: 'SHOW_NOT_ZERO',
  ShowZero: 'SHOW_ZERO',
});
export type JeCcFilter = EnumSet<typeof JeCcFilter>;

export type WithJeFormula<T> = T & { formula?: Formula; topIds?: ID[]; isFormulaNeedPropagation: boolean };

export type JeAuthorizeResponse = JeAuthorizeResponseDto;

export type JeOrderGenerated = JeOrderGeneratedDto;

export type JeDetails = JeDetailsBase & WithVersion<WithOptimisticLock<{
  jobTops: JeTop[];
  jobCostCenters: JeCostCenter[];
  jobComponents: JeComponent[];
  jobItems: JeItem[];
  jobOrders: JeOrder[];
  tops: JeTopRefItem[];
  uoms: JeUomRefItem[];
  salesTaxRates: JeStr[];
  memo?: string;
}>>;

export type JeDetailsProcess = JeDetails & {
  initialCostCenterId?: ID;
  initialCostCenter?: JeGetCostCenterResponse;
  jobOrdersForSchedule?: JeOrderForSchedule[];
};

export type JeOrder = WithDirtyType<JobOrderDetailsDto>;
export type JeStr = JeStrDto;
export type JeUomRefItem = JeUomRefItemDto;
export type JeTopRefItem = JeTopRefItemDto;
export type JeCcSupplier = CostCenterSupplierFinderListItemDto;

export type JeCostCenter = WithDirtyType<WithVersion<{
  jobCostCenterId?: ID;
  costCenterId: ID;
  name: string;
  code: number;
  cost?: number;
  memo?: string;
  suppliers: JeCcSupplier[];
  jobColorSelection: JeColorSelection[];
  preloaded: boolean;
}>>;
export type JeColorSelection = WithUniqueId<JeColorSelectionDto>;
export type JeTop = WithEntityState<WithDirtyType<{
  topId: ID;
  count: number;
  isFromGto: boolean;
  code: string;
  value?: Quantity<any>;
  gtoUom: GtoUom;
}>>;
export type JeComponent = WithEntityState<WithDirtyType<WithJeFormula<WithCreatedDate<{
  jobComponentId: ID;
  code: string;
  description: string;
  cost: number;
  memo: string;
  quantity: number;
  extension: number;
  componentId: ID;
  cuttingList?: JeCuttingListItem[];
  variationType: JeVariationType;
  variationStatus: JeVariationStatus;

  uomId: ID;
}>>>>;
export type JeItem = WithEntityState<WithDirtyType<WithJeFormula<WithCreatedDate<{
  jobItemId: ID;
  itemId: ID;
  jobComponentId?: ID;
  code: string;
  description: string;
  cost: number;
  uomId: ID;
  costCenterId: ID;
  // sorting is stored here just to have access to orderNumber, this allows to solve problems with sorting
  order?: JeOrder;
  isLocked: boolean;
  isSalesTax: boolean;
  quantity: number;
  quantityComponent?: number;
  extension: number;
  extensionComponent: number;
  variationType: JeVariationType;
  variationStatus?: JeVariationStatus;
  type: JeItemType;
  memo?: string;
  cuttingList?: JeCuttingListItem[];
}>>>>;
export type JeElement = JeItem | JeComponent;
export type JeComponentItem = JeComponentItemDto;

// tslint:disable-next-line:variable-name
export enum JeElementType { Component = 'Component', Item = 'Item' }

export type JeCuttingListItem = WithEntityState<WithUniqueId<WithCreatedDate<WithDirtyType<{
  sortOrder: number;
  sourceId: ID;
  sourceType: JeElementType;
  description: string;
  quantity: number;
  pieces?: number;
  dimension1Value?: number;
  dimension2Value?: number;
  dimension3Value?: number;
  hasPieces: boolean;
  hasDimension1Value: boolean;
  hasDimension2Value: boolean;
  hasDimension3Value: boolean;
}>>>>;

export type JeDetailsDirtyData = {
  isSave?: boolean;
  jobCostCenters: JeCostCenter[];
  jobComponents: JeComponent[];
  jobItems: JeItem[];
  jobTops: JeTop[];
  jobOrders: JeOrder[];
  salesTaxRates: JeStr[];
  memo?: string;
};

export type JeGetCostCenterResponse = WithVersion<{
  jobCostCenter: JeCostCenter;
  jobItems: JeItem[];
  jobOrders: JeOrder[];
}>;

export type JeGetComponentsResponse = {
  components: JeComponent[];
  items: JeItem[];
  affectedCostCenters: JeCostCenter[];
  newOrders: JeOrder[];
};

export type JeDetailsSave = JeDetailsDirtyData;

export type JeTopViewUsageRecord = {
  costCenterId: ID;
  recordId: ID;
  costCenterCode: number;
  recordCode: string;
};

export type JeOrderForSchedule = {
  jobOrderId: ID;
  costCenterId: ID;
  supplierId: ID;
  costCenterCode: number;
  costCenterName: string;
  orderNumber: number;
  supplierName: string;
  deliveryDate?: Date;
  startDate?: Date;
  amount: number;
};

export type JeTopViewUsage = {
  jobNumber: string;
  siteAddress: string;
  topName: string;
  records: JeTopViewUsageRecord[];
};

export type JeAddOrders = {
  jobOrdersForSchedule: JeOrderForSchedule[];
};

export type JeAddOrderStage = WithEntityState<JeOrderForSchedule>;
export interface JeMappingContext {
  getUom(id: ID): Maybe<JeUomRefItem>;

  getTop(id: ID): Maybe<JeTopRefItem>;
}

export type JeCcOrderDetails = JeCcOrderDetailsDto;
export type JeCcOrderDetailsOrder = { orderNumber: number; cost: number };
export type JeCcOrderDetailsItem = JeCcOrderDetailsListItemDto;

export const getJeUomRefItemId = (uom: JeUomRefItem) => uom.uomId;
export const getJeTopRefItemId = (uom: JeTopRefItem) => uom.topId;
export const getJeTopId = (top: JeTop) => top.topId;
export const getJeCostCenterId = (cc: JeCostCenter) => cc.costCenterId;
export const getJeComponentId = (component: JeComponent) => component.jobComponentId;
export const getJeItemId = (item: JeItem) => item.jobItemId;
export const getJeComponentItemId = (item: JeComponentItem) => `${item.costCenterId}:${item.itemId}`;
export const getJeCuttingListItemId = (item: JeCuttingListItem) => item.uniqueId;
export const getJeOrderId = (order: JeOrder) => order.orderId;
export const getJeColorSelectionId = (colorSelection: JeColorSelection) => colorSelection.uniqueId;
export const getJeStrId = (str: JeStr) => str.salesTaxRateId;
const JE_ADD_ORDER_STAGE_ENTITY_CONFIG: EntityConfig<JeAddOrderStage> = {
  costCenterName: { required: true },
  startDate: {
    required: true,
    type: DateTimeTypes.DateTime,
  },
};
export const validateJeAddOrderStageField = fieldValidatorByEntityConfig(JE_ADD_ORDER_STAGE_ENTITY_CONFIG);
export const isJeCcFiltered = (cc: JeCostCenter, filter: JeCcFilter): boolean => {
  if (filter === JeCcFilter.ShowAll) {
    return true;
  } else if (filter === JeCcFilter.ShowNotZero) {
    return cc.cost ? cc.cost !== 0 : false;
  } else {
    // Component CC is shown only for SelectAll
    return cc.code > 0 && cc.cost === 0 && filter === JeCcFilter.ShowZero;
  }
};
export const isJeSalesTaxCode = (code: string) => code === 'SALESTAX';

export const isJeHasEditableStatus = (details: {
  isAuthorized: boolean; isFinalized: boolean; isNotProceeding: boolean;
}) => !(details.isAuthorized || details.isFinalized || details.isNotProceeding);

export const isJeCanChangeAuthorization = (details: {
  isFinalized: boolean; isNotProceeding: boolean;
}) => !(details.isFinalized || details.isNotProceeding);

export const getJeTopQuantityType = (top: JeTop) => getUomQuantityType(top.gtoUom);

const isJeCuttingListFieldRequired = (entity: JeCuttingListItem, field: string) => {
  switch (field) {
    case 'description':
      return !entity.hasPieces
        && !entity.hasDimension1Value && !entity.hasDimension2Value && !entity.hasDimension3Value;
    case 'pieces':
      return entity.hasPieces;
    case 'dimension1Value':
      return entity.hasDimension1Value;
    case 'dimension2Value':
      return entity.hasDimension2Value;
    case 'dimension3Value':
      return entity.hasDimension3Value;
    default:
      return true;
  }
};

const JE_TOP_ENTITY_CONFIG: EntityConfig<JeTop> = {
  value: {
    required: true,
    editable: (top: JeTop) => !isJeContractPriceTop(top),
    type: getJeTopQuantityType,
  },
};

const JE_CUTTING_LIST_ITEM_ENTITY_CONFIG: EntityConfig<JeCuttingListItem> = {
  description: {
    required: isJeCuttingListFieldRequired,
    editable: true,
  },
  pieces: {
    required: isJeCuttingListFieldRequired,
    editable: true,
    type: NumberTypes.QuantityIntegral,
  },
  quantity: {
    required: isJeCuttingListFieldRequired,
    editable: true,
    type: NumberTypes.Quantity,
  },
  dimension1Value: {
    required: isJeCuttingListFieldRequired,
    editable: true,
    type: NumberTypes.Dimension,
  },
  dimension2Value: {
    required: isJeCuttingListFieldRequired,
    editable: true,
    type: NumberTypes.Dimension,
  },
  dimension3Value: {
    required: isJeCuttingListFieldRequired,
    editable: true,
    type: NumberTypes.Dimension,
  },
};

const JE_COMPONENT_ENTITY_CONFIG: EntityConfig<JeComponent> = {
  quantity: {
    required: true,
    editable: ({ formula, cuttingList }: JeComponent) => Boolean(isFormulaEmpty(formula) && isEmpty(cuttingList)),
    type: NumberTypes.SignedQuantity,
  },
  formula: {
    validator: fieldValidatorFrom(formulaValidator),
  },
  cuttingList: {
    validator: fieldValidatorFrom(nestedArrayValidator),
  },
  description: {
    required: true,
    type: TextTypes.Text255,
  },
};

const JE_ITEM_ENTITY_CONFIG: EntityConfig<JeItem> = {
  quantity: {
    required: true,
    editable: (item: JeItem) =>
      isFormulaEmpty(item.formula) && isEmpty(item.cuttingList) && isJeFreeItem(item),
    type: NumberTypes.SignedQuantity,
  },
  cost: {
    required: true,
    editable: isJeFreeOrComponentItem,
    type: NumberTypes.SignedPrice,
  },
  formula: {
    validator: fieldValidatorFrom(formulaValidator),
  },
  cuttingList: {
    validator: fieldValidatorFrom(nestedArrayValidator),
  },
  description: {
    required: true,
    editable: isJeFreeOrComponentItem,
    type: TextTypes.Text255,
  },
  order: {
    required: true,
    editable: isJeFreeOrComponentItem,
  },
  isSalesTax: {
    editable: isJeFreeOrComponentItem,
  },
  isLocked: {
    editable: isJeFreeOrComponentItem,
  },
};

export const validateJeTopField = fieldValidatorByEntityConfig(JE_TOP_ENTITY_CONFIG);
export const jeTopFieldState = fieldStateByEntityConfig(JE_TOP_ENTITY_CONFIG);

export const jeComponentFieldState = fieldStateByEntityConfig(JE_COMPONENT_ENTITY_CONFIG);
export const validateJeComponentField = fieldValidatorByEntityConfig(JE_COMPONENT_ENTITY_CONFIG);

export const validateJeCuttingListItemField = fieldValidatorByEntityConfig(JE_CUTTING_LIST_ITEM_ENTITY_CONFIG);
export const validateJeCuttingListItem = entityValidatorByEntityConfig(JE_CUTTING_LIST_ITEM_ENTITY_CONFIG);
export const jeCuttingListItemFieldState = fieldStateByEntityConfig(JE_CUTTING_LIST_ITEM_ENTITY_CONFIG);
export const complementJeCuttingListItem = entityStateMerger(errorsEntityState(validateJeCuttingListItem));

export const jeItemFieldState = fieldStateByEntityConfig(JE_ITEM_ENTITY_CONFIG);
export const validateJeItemField = fieldValidatorByEntityConfig(JE_ITEM_ENTITY_CONFIG);
export const validateJeItem = entityValidatorByEntityConfig(JE_ITEM_ENTITY_CONFIG);
export const complementJeItem = entityStateMerger(errorsEntityState(validateJeItem));

/**
 * We have to reset quantity to '1' if cuttingList or formula becomes empty
 */
export const jeQuantityResetSetter = (entity: JeItem | JeComponent, name: string, _: any) => {
  switch (name) {
    case 'formula': {
      const { formula } = entity;
      if (isNil(formula) || formula.valid && isEmpty(formula.expression)) {
        return setEntityField(entity, 'quantity', 1);
      } else {
        return entity;
      }
    }
    case 'cuttingList': {
      const { cuttingList } = entity;
      if (isEmpty(cuttingList)) {
        return setEntityField(entity, 'quantity', 1);
      } else {
        return entity;
      }
    }
    default:
      return entity;
  }
};

export const jeOrderFromGenerated = (order: JeOrderGenerated, orderNumber: number): JeOrder => ({
  ...order,
  orderNumber,
  attachments: [],
  delivery: JobOrderDelivery.Schedule,
  amount: 0,
  allowReprint: false,
});

export const jeOrderFromOrderDetails = jobOrderDetailsToDto;
export const jeOrderToOrderDetails = jobOrderDetailsFromDto;

export const newJeCuttingListItem = (uomId: ID,
                                     sortOrder: number,
                                     sourceType: JeElementType,
                                     sourceId: ID,
                                     mappingContext: JeMappingContext) => {
  const uom = mappingContext.getUom(uomId);

  return {
    uniqueId: generateJeCuttingListItemId(),
    sortOrder,
    sourceType,
    sourceId,
    description: '',
    quantity: 1,
    hasPieces: uom && uom.usePieces || false,
    hasDimension1Value: uom && !isEmpty(uom.dimension1) || false,
    hasDimension2Value: uom && !isEmpty(uom.dimension2) || false,
    hasDimension3Value: uom && !isEmpty(uom.dimension3) || false,
    createdDate: currentDate(),
  };
};

export const jeStrToFinder = (str: JeStr) => <SalesTaxRateListItem>({
  id: str.salesTaxRateId,
  name: '',
  rate: str.ratePct,
  isDefault: false,
});

export const jeStrFromFinder = (str: SalesTaxRateListItem) => <JeStr>({
  salesTaxRateId: str.id,
  ratePct: str.rate,
});

export function jeDetailsFromDto(dto: JeDetailsDto): JeDetails {
  const mappingContext = createInitJeMappingContext(dto);
  const ordersById = keyBy(dto.jobOrders, 'orderId');

  return {
    ...cast<JeDetails>(omit(dto, 'jobComponents', 'jobCostCenters', 'jobItems')),
    jobCostCenters: map(dto.jobCostCenters.filter(isJeTypicalCostCenter), jeCostCenterFromDto),
    jobComponents: map(dto.jobComponents, (component) => jeComponentFromDto(component, mappingContext)),
    jobItems: map(
      dto.jobItems,
      (item) => jeItemFromDto(item, item.orderId ? ordersById[item.orderId] : void 0, mappingContext)),
    jobTops: map(dto.jobTops, (top) => jeTopFromTopDto(top, mappingContext)),
    memo: dto.memo,
  };
}

export function jeTopFromTopDto(top: JeTopDto, mappingContext: JeMappingContext): JeTop {
  const refTop = mappingContext.getTop(top.topId)!; // tslint:disable-line:no-non-null-assertion
  return {
    ...top,
    code: refTop.code,
    value: isNotNil(top.value) ? createUomQuantity(top.value, refTop.gtoUom) : void 0,
    gtoUom: refTop.gtoUom,
  };
}

export function jeDetailsFromDtoWithMappingContext(dto: JeDetailsDto, mappingContext: JeMappingContext): JeDetails {
  const ordersById = keyBy(dto.jobOrders, 'orderId');

  return {
    ...cast<JeDetails>(omit(dto, 'jobComponents', 'jobCostCenters', 'jobItems')),
    jobCostCenters: map(dto.jobCostCenters, jeCostCenterFromDto),
    jobComponents: map(dto.jobComponents, (component) => jeComponentFromDto(component, mappingContext)),
    jobItems: map(
      dto.jobItems,
      (item) => jeItemFromDto(item, item.orderId ? ordersById[item.orderId] : void 0, mappingContext)),
    jobTops: map(dto.jobTops, (top) => jeTopFromTopDto(top, mappingContext)),
    memo: dto.memo,
  };
}

export function jeGetComponentsResponseFromDto(dto: JeGetComponentsResponseDto,
                                               mappingContext: JeMappingContext): JeGetComponentsResponse {
  return {
    affectedCostCenters: dto.affectedCostCenters.map(jeCostCenterFromDto),
    components: dto.components.map((component) =>
      jeComponentFromWithItemsDto(component, mappingContext)).map(setCreatedDate),
    items: <JeItem[]>flatMap(
      dto.components,
      (component) => component.items.map((item) => jeItemFromComponentItemDto(item, mappingContext))),
    newOrders: [],
  };
}

export function jeComponentFromWithItemsDto(dto: JeComponentWithItemsDto,
                                            mappingContext: JeMappingContext): JeComponent {
  return {
    ...cast<JeComponent>(omit(dto, 'cuttingList', 'name')),
    description: dto.name,
    variationType: JeVariationType.None,
    variationStatus: JeVariationStatus.Unconfirmed,
    isFormulaNeedPropagation: false,
    topIds: dto.topIds ? normalizeTakeOffParams(dto.topIds) : void 0,
    cuttingList: [],
  };
}

export function jeComponentFromDto(dto: JeComponentDto, mappingContext: JeMappingContext): JeComponent {
  return {
    ...<JeComponent>omit(dto, 'cuttingList'),
    isFormulaNeedPropagation: false,
    topIds: dto.topIds ? normalizeTakeOffParams(dto.topIds) : void 0,
    cuttingList: (dto.cuttingList || [])
      .map((item) => jeCuttingListItemFromDto(
        dto.uomId,
        dto.jobComponentId,
        JeElementType.Component,
        item,
        mappingContext)),
  };
}

export function jeCuttingListItemFromDto(uomId: Maybe<ID>,
                                         sourceId: ID,
                                         sourceType: JeElementType,
                                         dto: JeCuttingListItemDto,
                                         mappingContext: JeMappingContext): JeCuttingListItem {
  const uom = uomId && mappingContext.getUom(uomId);

  return {
    ...dto,
    uniqueId: generateJeCuttingListItemId(),
    sourceType,
    sourceId, // tslint:disable-line:no-non-null-assertion
    hasPieces: uom && uom.usePieces || false,
    hasDimension1Value: uom && !isEmpty(uom.dimension1) || false,
    hasDimension2Value: uom && !isEmpty(uom.dimension2) || false,
    hasDimension3Value: uom && !isEmpty(uom.dimension3) || false,
  };
}

export function jeItemFromComponentItemDto(dto: JeComponentItemDto, mappingContext: JeMappingContext): JeItem {
  return {
    ...cast<JeItem>(omit(dto, ['name', 'isActive'])),
    description: dto.name,
    quantityComponent: dto.quantity,
    type: JeItemType.Component,
    isSalesTax: false,
    isLocked: false,
    isFormulaNeedPropagation: false,
    variationType: JeVariationType.None,
    variationStatus: JeVariationStatus.Unconfirmed,
    createdDate: currentDate(),
  };
}

export function isJeCcCompleteDto(dto: JeCcRefDto | JeCcCompleteDto): dto is JeCcCompleteDto {
  return isNotNil(dto['jobColorSelection']);
}

export function jeCostCenterFromDto(dto: JeCcRefDto | JeCcCompleteDto): JeCostCenter {
  return {
    ...pick(dto, 'jobCostCenterId', 'costCenterId', 'code', 'name', 'memo', 'cost', 'version'),
    suppliers: isJeCcCompleteDto(dto) ? dto.suppliers : [],
    jobColorSelection: isJeCcCompleteDto(dto) && dto.jobColorSelection ? dto.jobColorSelection.map(bindUniqueId) : [],
    preloaded: isJeComponentCostCenter(dto),
  };
}

export function jeCostCenterFromFinder(found: CostCenterFinderListItem): JeCostCenter {
  return {
    costCenterId: found.id,
    code: found.code,
    name: found.name,
    jobColorSelection: [],
    suppliers: found.suppliers || [],
    version: 1,
    cost: 0,
    preloaded: true,
  };
}

export function jeCostCenterItemsFromFinder(costCenterId: ID,
                                            ids: ID[],
                                            items: ComponentItemFinderListItem[],
                                            mappingContext: JeMappingContext): JeItem[] {
  return items.map((item, i) => jeCostCenterItemFromFinder(costCenterId, ids[i], item, mappingContext));
}

export function jeCostCenterItemFromFinder(costCenterId: ID,
                                           jobItemId: ID,
                                           found: ComponentItemFinderListItem,
                                           mappingContext: JeMappingContext): JeItem {
  return {
    costCenterId,
    jobItemId,
    isSalesTax: found.isSalesTax,
    isLocked: false,
    type: JeItemType.Item,
    quantity: 1,
    cost: found.cost,
    extension: found.cost,
    extensionComponent: 0,
    itemId: found.id,
    code: found.code,
    description: found.name,
    uomId: found.uomId,
    formula: found.formula,
    topIds: found.topIds,
    memo: found.memo,
    isFormulaNeedPropagation: false,
    variationType: JeVariationType.None,
    variationStatus: JeVariationStatus.Unconfirmed,
    createdDate: currentDate(),
    cuttingList: [],
  };
}

export function jeCostCenterResponseFromDto(costCenterId: ID,
                                            costCenter: JeCcCompleteDto,
                                            mappingContext: JeMappingContext): JeGetCostCenterResponse {
  const ordersById = keyBy(costCenter.jobOrders, 'orderId');

  return {
    jobCostCenter: jeCostCenterFromDto(costCenter),
    jobItems: costCenter.jobItems.map((item) => jeItemFromDto(
      item,
      item.orderId ? ordersById[item.orderId] : void 0,
      mappingContext)),
    jobOrders: costCenter.jobOrders,
  };
}

export function jeItemFromDto(dto: JeItemDto, order: Maybe<JeOrder>, mappingContext: JeMappingContext): JeItem {
  return {
    ...<JeItem>omit(dto, 'orderId'),
    jobItemId: isJeSalesTaxCode(dto.code) ? `${dto.costCenterId}:${dto.orderId}` : dto.jobItemId,
    type: dto.jobComponentId ? JeItemType.Component
      : (isJeSalesTaxCode(dto.code) ? JeItemType.SalesTax : JeItemType.Item),
    topIds: dto.topIds ? normalizeTakeOffParams(dto.topIds) : void 0,
    isFormulaNeedPropagation: false,
    cuttingList: (dto.cuttingList || [])
      .map((item) => jeCuttingListItemFromDto(
        dto.uomId,
        dto.jobItemId,
        JeElementType.Item,
        item,
        mappingContext)),
    order,
  };
}

export const jeFormulaVariableFromTopDto = ({ topId, code, isActive }: JeTopRefItemDto) =>
  ({ id: topId, name: code, active: isActive });
export const jeFormulaVariableFromTop = jeFormulaVariableFromTopDto;

export function mergeLoadedCostCenter(current: JeCostCenter, loaded: JeCostCenter): JeCostCenter {
  return {
    ...current,
    suppliers: loaded.suppliers,
    cost: loaded.cost,
    version: loaded.version,
    memo: loaded.memo,
    jobColorSelection: loaded.jobColorSelection,
    preloaded: true,
  };
}

export function isJeTypicalCostCenter(costCenter: JeCostCenter | JeCcRefDto): boolean {
  return !isJeComponentCostCenter(costCenter);
}

export function isJeComponentCostCenter(costCenter: JeCostCenter | JeCcRefDto): boolean {
  return costCenter.code === 0;
}

export function generateJeCuttingListItemId(): ID {
  return uniqueId('cutting-list-item:');
}

export function isJeItemFromComponent(item: { jobComponentId?: ID }): boolean {
  return !isNil(item.jobComponentId);
}

export function isJeFreeItem(item: JeItem): boolean {
  return item.type === JeItemType.Item;
}

export function isJeItem(element: JeElement): element is JeItem {
  return isNotNil(element['jobItemId']);
}

export function isJeFreeOrComponentItem(item: JeItem): boolean {
  return item.type === JeItemType.Item || item.type === JeItemType.Component;
}

export function isJeSalesTaxItem(item: JeItem): boolean {
  return item.type === JeItemType.SalesTax;
}

export function createJeTop(topRefItem: JeTopRefItem): JeTop {
  return {
    topId: topRefItem.topId,
    code: topRefItem.code,
    isFromGto: false,
    value: createUomQuantity(0, topRefItem.gtoUom),
    count: 1,
    gtoUom: topRefItem.gtoUom,
  };
}

export function getJeComponentTopIds(component: JeComponent): ID[] {
  return component.topIds || [];
}

export function getJeItemTopIds(item: JeItem): ID[] {
  return item.topIds || [];
}

export const jeCostCenterToSaveDto = (cc: JeCostCenter) => <JeCcSaveDto>pick(
  cc,
  ['jobCostCenterId', 'costCenterId', 'version', 'dirtyType', 'memo']);

export const jeCuttingListToDto = (cuttingList: JeCuttingListItem[]) =>
  <JeCuttingListItemDto[]>cuttingList.map((item) => pick(
    item,
    compact([
      'sortOrder',
      'description',
      item.hasPieces && 'pieces',
      item.hasDimension1Value && 'dimension1Value',
      item.hasDimension2Value && 'dimension2Value',
      item.hasDimension3Value && 'dimension3Value'])));

export const jeComponentToSaveDto = (component: JeComponent) => ({
  ...<JeComponentSaveDto>pick(
    component,
    [
      'jobComponentId', 'componentId', 'cost', 'quantity', 'formula', 'dirtyType', 'memo',
      'description', 'variationType', 'variationStatus', 'uomId',
    ]),
  cuttingList: jeCuttingListToDto(component.cuttingList || []),
});

export const jeItemToSaveDto = (item: JeItem) => ({
  ...<JeItemSaveDto>pick(
    item,
    [
      'jobItemId', 'itemId', 'costCenterId', 'jobComponentId', 'cost', 'quantity', 'quantityComponent',
      'formula', 'memo', 'dirtyType', 'memo', 'description', 'variationType', 'variationStatus', 'isLocked',
      'isSalesTax', 'uomId',
    ]),
  orderId: item.order && item.order.orderId,
  cuttingList: jeCuttingListToDto(item.cuttingList || []),
});

export const jeTopToSaveDto = (top: JeTop) =>
  <JeTopSaveDto>pick(top, ['topId', 'count', 'value', 'isFromGto']);

export const jeOrderToSaveDto = (order: JobOrderDetailsDto) => order;
export const jeStrToSaveDto = (str: JeStrDto) => str;

export function jeDetailsDirtyDataToDto(model: JeDetailsSave,
                                        isSave: boolean = false,
                                        isReprice: boolean = false): JeDirtyDataDto {
  const componentCC = model.jobCostCenters.filter(isJeComponentCostCenter);

  return {
    jobCostCenters: model.jobCostCenters.filter(isJeTypicalCostCenter).map(jeCostCenterToSaveDto),
    jobComponents: model.jobComponents.map(jeComponentToSaveDto),
    jobItems: model.jobItems.map(jeItemToSaveDto),
    jobTops: model.jobTops.map(jeTopToSaveDto),
    jobOrders: model.jobOrders.map(jeOrderToSaveDto),
    salesTaxRates: map(model.salesTaxRates, jeStrToSaveDto),
    memo: componentCC.length ? componentCC[0].memo : model.memo,
    isSave,
    isReprice,
  };
}

export const jeDetailsToEmptyDirtyData = (details: JeDetails) => <JeDetailsDirtyData>({
  jobCostCenters: [],
  jobComponents: [],
  jobItems: [],
  jobTops: details.jobTops,
  jobOrders: [],
  salesTaxRates: details.salesTaxRates,
});

export const jeUomRefItemFromDto = (dto: UomDetailsDto): JeUomRefItem => ({
  uomId: dto.id,
  code: dto.code,
  usePieces: dto.usePieces,
  dimension1: dto.dimension1 || dto.dimension1User,
  dimension2: dto.dimension2 || dto.dimension2User,
  dimension3: dto.dimension3 || dto.dimension3User,
});

export const jeTopRefItemFromDto = (dto: TakeOffParamDto): JeTopRefItem => ({
  topId: dto.takeOffParamId,
  code: dto.code,
  gtoUom: dto.gtoUom!, // tslint:disable-line:no-non-null-assertion
  isActive: dto.isActive,
});

const COMPONENT_FIELDS_AFFECTING_PRICE = keyBy(['quantity', 'formula', 'cuttingList']);
const ITEM_FIELDS_AFFECTING_PRICE = keyBy(['isSalesTax', 'quantity', 'cost', 'formula', 'cuttingList', 'order']);
export const isJeComponentFieldAffectsPrice = (name: string) => isNotNil(COMPONENT_FIELDS_AFFECTING_PRICE[name]);
export const isJeItemFieldAffectsPrice = (name: string) => isNotNil(ITEM_FIELDS_AFFECTING_PRICE[name]);
export const isJeCuttingListItemFieldAffectsPrice = (name: string) => name !== 'description';

export const isJeContractPriceTop = (top: JeTop) => top.code === TOP_CODE_CONTRACT_PRICE;

export const isJeRecordForComponent = (record: JeTopViewUsageRecord) => record.costCenterCode === 0;

export const isJeCcNotPreloaded = (cc: JeCostCenter) => !cc.preloaded;

export const createInitJeMappingContext = (dto: JeDetailsDto | JeDetails): JeMappingContext => {
  const uoms = keyBy(dto.uoms, 'uomId');
  const tops = keyBy(dto.tops, 'topId');

  return {
    getTop: (id) => tops[id],
    getUom: (id) => uoms[id],
  };
};

export function isJeComponent(element: JeElement): element is JeComponent {
  return isNil(element['jobItemId']);
}

export const hasJeElementUncofirmedVariation = (element: JeElement) =>
  element.variationType !== JeVariationType.None && element.variationStatus === JeVariationStatus.Unconfirmed;

export const isJeElementHasInvalidFormula = ({ formula }: JeElement) => formula && !formula.valid;

export const jeCostCenterToFinderListItem = (cc: JeCostCenter): CostCenterFinderListItem =>
  ({ id: getJeCostCenterId(cc), code: cc.code, name: cc.name });

export const isJeOrderProduced = (order: JeOrder) => isNotNil(order.producedDate);

export const isJeItemLocked = (item: JeItem) => item.isLocked;

export const isJeOrder0 = (order: JeOrder) => order.orderNumber === 0;

export const jeVariationComparator = composeComparators(
  valueComparator('variationStatus'),
  valueComparator('variationType'));
export const jeFormulaValueComparator = valueComparator(isFormulaEmpty);
export const jeCuttingListValueComparator = valueComparator(isEmpty);
